import { z } from "zod/v4";
import { CreatedAt, UpdatedAt } from "../system";
import { BrandId, BrandIdSchema, ItemId, ItemIdSchema, SaleId, SaleIdSchema } from "../../id";
import { DocumentSnapshotLike } from "../../firestore";
import { ItemKind } from "./item-kind";
import { DocumentStatus } from "../../common"
import { Period, PeriodState, PeriodRequest } from "../common";

export const SaleCommon = z.object({
  name: z.string().describe("セールの名前。"),
  percentage: z.int().min(1).max(100).describe("割引のパーセンテージ。"),
  sale_period: Period.describe("セールの期間"),
  status: DocumentStatus,
})

export const SaleKindAll = z.intersection(
  SaleCommon,
  z.object({
    kind: z.literal("all"),
  })
).describe("全商品を対象にしたセール。")

export const SaleKindItemKind = z.intersection(
  SaleCommon,
  z.object({
    kind: z.literal("item-kind"),
    value: ItemKind,
  })
).describe("アイテム種別指定でのセール。")

export const SaleKindBrand = z.intersection(
  SaleCommon,
  z.object({
    kind: z.literal("brand"),
    value: BrandIdSchema,
  })
).describe("ブランドを対象にしたセール。")

export const SaleKindCollection = z.intersection(
  SaleCommon,
  z.object({
    kind: z.literal("collection"),
    value: z.array(ItemIdSchema),
  })
).describe("ある対象のうち、１つでもアイテムを持っていると残りのものが割引になるセール。")

export const SaleKindItems = z.intersection(
  SaleCommon,
  z.object({
    kind: z.literal("items"),
    value: z.array(ItemIdSchema),
  })
).describe("アイテムID指定でのセール。")

export type SaleDocumentMeta = z.infer<typeof SaleDocumentMeta>
export const SaleDocumentMeta = z.union([
  SaleKindAll,
  SaleKindItemKind,
  SaleKindBrand,
  SaleKindCollection,
  SaleKindItems,
])

const SaleCommonRequest = z.object({
  name: z.string().describe("セールの名前。"),
  percentage: z.int().min(1).max(100).describe("割引のパーセンテージ。"),
  salePeriod: PeriodRequest.describe("セールの期間"),
  status: DocumentStatus,
})

const SaleKindAllRequest = z.intersection(
  SaleCommonRequest,
  z.object({
    kind: z.literal("all"),
  })
)

const SaleKindItemKindRequest = z.intersection(
  SaleCommonRequest,
  z.object({
    kind: z.literal("item-kind"),
    value: ItemKind,
  })
)

const SaleKindBrandRequest = z.intersection(
  SaleCommonRequest,
  z.object({
    kind: z.literal("brand"),
    value: BrandIdSchema,
  })
)

const SaleKindCollectionRequest = z.intersection(
  SaleCommonRequest,
  z.object({
    kind: z.literal("collection"),
    value: z.array(ItemIdSchema),
  })
)

const SaleKindItemsRequest = z.intersection(
  SaleCommonRequest,
  z.object({
    kind: z.literal("items"),
    value: z.array(ItemIdSchema),
  })
)

export type CreateSaleRequest = z.infer<typeof CreateSaleRequest>
export const CreateSaleRequest = z.union([
  SaleKindAllRequest,
  SaleKindItemKindRequest,
  SaleKindBrandRequest,
  SaleKindCollectionRequest,
  SaleKindItemsRequest,
])

export type UpdateSaleRequest = z.infer<typeof UpdateSaleRequest>
export const UpdateSaleRequest = z.union([
  SaleKindAllRequest,
  SaleKindItemKindRequest,
  SaleKindBrandRequest,
  SaleKindCollectionRequest,
  SaleKindItemsRequest,
])

export type SaleDocumentBody = z.infer<typeof SaleDocumentBody>
export const SaleDocumentBody = z.intersection(
  SaleDocumentMeta,
  z.object({
    sale_period_state: PeriodState.describe("セール期間の状態（システム自動計算）"),
    created_at: CreatedAt,
    updated_at: UpdatedAt,
  })
)

export const SaleDocument = z.intersection(
  SaleDocumentBody,
  z.object({
    id: SaleIdSchema,
  })
)

export const SaleItemDenormalized = z.union([
  // SaleKindAll,
  // SaleKindItemKind,
  SaleKindBrand,
  SaleKindCollection,
  SaleKindItems,
]).describe("非正規化されたセール情報。アイテムにコピーされるもの。")
export type SaleItemDenormalized = z.infer<typeof SaleItemDenormalized>

export const SaleAppliedDenormalized = z.union([
  SaleKindAll,
  SaleKindItemKind,
  SaleKindBrand,
  SaleKindCollection,
  SaleKindItems,
]).describe("非正規化されたセール情報。インベントリなどで用いる、最終的に適用されたセールの情報。この値は履歴なので更新されない。")
export type SaleAppliedDenormalized = z.infer<typeof SaleAppliedDenormalized>

export type SaleDocument = z.infer<typeof SaleDocument>

/**
 * calculateEffectivePrice で使用するセール情報の共通型
 * SaleDocument と SaleItemDenormalized の両方を受け入れる
 */
export type SaleForCalculation = {
  kind: "all" | "item-kind" | "brand" | "collection" | "items"
  percentage: number
  sale_period: { start_at: number; end_at: number | null }
  status: "public" | "hidden"
  value?: ItemKind | BrandId | ItemId[]
}

export class SaleDocuments {

  /**
   * 適用可能なセールから最終価格を計算する
   * 
   * バックエンドの item-purchase-use-case.ts の findApplicableSale と同じロジックを実装:
   * - sale_period.start_at <= now かつ (sale_period.end_at === null または now < sale_period.end_at)
   * - status === "public"
   * - 複数のセールが適用可能な場合、最も割引率(percentage)が高いものを選択
   * 
   * @param originalPrice 元の価格
   * @param sales セール情報の配列（/sales コレクションと item.sales の両方を含む）
   * @param itemKind アイテムの種別
   * @param brandIds アイテムのブランドID配列
   * @param itemId アイテムID
   * @param ownedItemIds ユーザーが所持しているアイテムID配列（collectionセール判定用）
   * @param now 現在時刻（デフォルトは Date.now()）
   */
  static calculateEffectivePrice(
    originalPrice: number,
    sales: SaleForCalculation[],
    itemKind: ItemKind,
    brandIds: BrandId[],
    itemId: ItemId,
    ownedItemIds: ItemId[] = [],
    now: number = Date.now()
  ): { price: number; appliedSale: SaleForCalculation | null } {
    const applicableSales = sales.filter(sale => {
      // ステータスチェック
      if (sale.status !== "public") return false

      // 期間チェック（バックエンドの findApplicableSale と同じロジック）
      const { start_at, end_at } = sale.sale_period
      if (now < start_at) return false
      if (end_at !== null && end_at <= now) return false

      // 種別ごとの適用条件チェック
      switch (sale.kind) {
        case "all":
          return true
        case "item-kind":
          return sale.value === itemKind
        case "brand":
          return brandIds.includes(sale.value as BrandId)
        case "items":
          return Array.isArray(sale.value) && sale.value.includes(itemId)
        case "collection": {
          // collectionセールは、コレクション内の他のアイテムを所持している場合のみ適用
          if (!Array.isArray(sale.value)) return false
          if (!sale.value.includes(itemId)) return false
          const otherItemsInCollection = sale.value.filter(id => id !== itemId)
          return otherItemsInCollection.some(id => ownedItemIds.includes(id))
        }
        default:
          return false
      }
    })

    if (applicableSales.length === 0) {
      return { price: originalPrice, appliedSale: null }
    }

    // 最も割引率が高いセールを選択
    const bestSale = applicableSales.reduce((best, current) =>
      best.percentage < current.percentage ? current : best
    )

    const discountedPrice = Math.floor(originalPrice * (100 - bestSale.percentage) / 100)
    return { price: discountedPrice, appliedSale: bestSale }
  }

  static toDocumentData(doc: SaleDocument): object {
    return {
      ...doc,
      // id は document path に入るので入れない
      id: undefined,
    };
  }

  static fromDocumentSnapshot(doc: DocumentSnapshotLike | null): SaleDocument | null {
    if (!doc) {
      return null;
    }
    const data = doc.data();
    if (data === undefined) {
      return null;
    }

    return {
      id: SaleId(doc.id),
      name: data["name"],
      percentage: data["percentage"],
      status: data["status"],
      sale_period: data["sale_period"],
      sale_period_state: data["sale_period_state"],
      kind: data["kind"],
      value: data["value"],
      created_at: data["created_at"],
      updated_at: data["updated_at"],
    }
  }
}
